import SharedLegalLayout from './layout'

/**
 * PrivacyPolicy
 *
 * The Privacy Policy page
 */
const PrivacyPolicy = () => (
  <SharedLegalLayout heading="Privacy Policy">
    <section>
      <p>
        Rescue X Training and Consulting, LLC (“us”, “our”, or “we”) institutes the terms of this
        Privacy Policy to ensure that personally identifiable information (“PII”) is maintained in
        a safe, secure, and responsible manner. The following describes how we collect, use, and
        share the PII obtained from and about individuals who use our mobile application, Xverse,
        the website located at{' '}
        <a href="https://rescuextraining.com" target="_blank" rel="noopener noreferrer">
          rescuextraining.com
        </a>
        , and our other online products or services (collectively, the “Services”) or when you
        otherwise interact with us or receive a communication from us. This Privacy Policy applies
        only to information collected through the Services and does not apply to information that
        we may collect outside the use of the Services such as over the phone or by email or mail.
        By using these Services, you are agreeing to the collection and use of information you
        provide to these Services as described below.
      </p>

      <p>
        Please read this Privacy Policy carefully to understand our practices. By using our
        Services, you consent to the collection, use, and sharing of your information as described
        below.
      </p>
    </section>

    {/* 1. Information We Collect */}
    <section>
      <h2>1. Information We Collect</h2>
      <p>
        We may collect PII for purposes related to our business, to improve the content of the
        Services, or for any other commercial purpose. The only PII collected from you has been
        voluntarily submitted and provided to us through your use of the Services or in response to
        requests for information. We may collect such data for processing that is necessary for
        providing our services as carried out in our legitimate interests.
      </p>

      <ol className="list-[lower-alpha]">
        <li>
          <b>User-provided Information.</b> When you use the Services, you may provide, and we may
          collect, PII. The types of information that we may collect include but are not limited
          to: name, address, email address, certification information, and phone number. PII also
          includes other information, such as geographic areas or preferences, when any such
          information is linked to information that identifies a specific individual.
        </li>
        <li>
          <b>Automatically Collected Information.</b> When you use the Services, we may
          automatically record certain information from your device by using various types of
          technology, including cookies. This “automatically collected” information may include IP
          address or other device address or ID, web browser and/or device type, device settings,
          user-agent string, the pages or content you view or interact with on the Services, and
          the dates and times of your access or use of the Services.
        </li>
        <li>
          <b>Derivative Data.</b> We may automatically collect data when you access the Services,
          such as your native actions that are integral to the Services, actions taken when
          creating entries, editing entries, and uploading media to the Services. Any media
          uploaded in this fashion will be collected and stored on our servers.
        </li>
        <li>
          <b>Location Information.</b> We may receive and process information about your location.
          For example, with your consent, we may collect information about the specific location of
          your mobile device (for example, by using GPS or Bluetooth). We may also receive location
          information from you when you choose to share such information on the Services, including
          by associating your content with a location, or we may derive your approximate location
          from other information about you, including your IP address.
        </li>
        <li>
          <b>Information from Other Sources.</b> We may obtain information, including PII, from
          third parties and sources other than the Services. If information from other sources is
          combined or associated with PII collected through the Services, this combined information
          will be treated as PII in accordance with this Privacy Policy. We may also receive
          information about you, including log and usage data and cookie information, from
          third-party sites that integrate our services, including embeds and advertising
          technology.
        </li>
      </ol>
    </section>

    {/* 2. How Collected Information is Used */}
    <section>
      <h2>2. How Collected Information is Used</h2>
      <p>
        We may use the information that is collected in a variety of ways in providing the
        Services, including, but not limited to, the following:
      </p>
      <ol className="list-[lower-alpha]">
        <li>
          <b>Operations.</b> We may use PII to operate, maintain, enhance, and provide all features
          of the Services, to provide the services and information that you request, to respond to
          comments and questions, and to provide user support.
        </li>

        <li>
          <b>Improvements.</b> We may use collected PII to understand and analyze usage trends and
          preferences, to improve the Services, and to develop new services, features, and
          functionality.
        </li>

        <li>
          <b>Communications.</b> We may use PII to communicate with you for administrative purposes
          such as customer service, updates on policies or terms, and communication relating to
          services, via the communication method chosen by you. You may opt out of receiving such
          communications at any time via a link in the email you receive, by replying STOP to SMS
          or text messages, or by contacting us as provided in the “Contact” section. You
          understand and acknowledge that certain features of the Services rely on such
          communications and by opting out you may lose the availability and functionality of some
          aspects of the Services.
        </li>

        <li>
          <b>Advertisements.</b> We may use third-party programmatic ad exchanges to show
          advertisements relevant to your interests. In providing those ads, those third parties
          collect information, including log and usage data and information from cookies. These
          third parties will not receive information from your account.
        </li>

        <li>
          <b>Cookies and Tracking Technology.</b> We may use automatically collected information
          and other information collected on the Services through cookies and similar technologies
          to: (i) personalize the Services; (ii) monitor and analyze the effectiveness of the
          Services; and (iii) monitor aggregate site usage metrics such as number of visitors and
          pages viewed. Cookies may be either “persistent” cookies or “session” cookies. A
          persistent cookie will be stored by a web browser and will remain valid until its set
          expiration date, unless deleted by you prior to the expiration date; a session cookie
          will expire at the end of your use of the Services when the web browser is closed.
        </li>

        <li>
          <b>Eligibility Review.</b> Certain administrators will review information uploaded by you
          to verify authenticity of licenses and certifications and determine eligibility for
          particular trainings.
        </li>
      </ol>
    </section>

    {/* 3. How Information is Shared */}
    <section>
      <h2>3. How Information is Shared</h2>
      <p>
        Except as described in this Privacy Policy, we will not intentionally disclose your PII to
        third parties without your explicit consent. We may disclose information to third parties
        with your consent, as well as in the following circumstances:
      </p>
      <ol className="list-[lower-alpha]">
        <li>
          <b>Unrestricted Information.</b>
          Content generated by you and posted to the Services, or any of our associated social
          media sites, is your sole and exclusive responsibility and we have no responsibility or
          liability for such content. Any information you voluntarily display to the public on the
          Services will be available to any individual who has access to that content. We cannot
          and will not guarantee the safety or security of your user generated content. Further,
          search engines may index your user generated content such that it may be found on and
          through common search engines. We cannot and will not guarantee the safety or security of
          your user generated content.
        </li>

        <li>
          <b>Service Providers.</b> In some events we may work with service providers who provide
          website, application development, hosting, maintenance, and other services. These third
          parties may have access to or process PII as part of providing such services. We limit
          the information provided to these service providers to that which is reasonably necessary
          for them to perform their functions.
        </li>

        <li>
          <b>Non-Personally Identifiable Information.</b>
          We may make certain automatically-collected, aggregated, or otherwise non-personally
          identifiable information available to third parties for various purposes, including: (i)
          compliance with reporting obligations; and (ii) business or marketing purposes.
        </li>

        <li>
          <b>Legal Compliance.</b>
          We may disclose PII or other information if required to do so by law or in the good-faith
          belief that such action is necessary to comply with applicable laws, in response to a
          valid court order, judicial or other government subpoena or warrant, or to otherwise
          cooperate with law enforcement or other government agencies. We also reserve the right to
          disclose PII or other information that we believe, in good faith, is appropriate or
          necessary to: (i) take precautions against liability; (ii) protect from fraudulent,
          abusive, or unlawful uses or activity; (iii) investigate and defend against any
          third-party claims or allegations; or (iv) protect our property or other legal rights,
          enforce our contracts, or protect the rights, property, or safety of others.
        </li>

        <li>
          <b>Change in Structure/Ownership.</b>
          Information, including PII, may be disclosed and otherwise transferred to an acquirer,
          successor, or assignee as part of any merger, acquisition, debt financing, sale of
          assets, or similar transaction, as well as in the event of an insolvency, bankruptcy, or
          receivership in which information is transferred to one or more third parties as one of
          our business assets and only if the recipient of the PII commits to a privacy policy that
          has terms substantially consistent with this Privacy Policy.
        </li>
      </ol>
    </section>

    {/* 4. Your Rights */}
    <section>
      <h2>4. Your Rights</h2>
      <p>
        We respect your privacy rights and provide you with reasonable access to the PII that you
        may have provided through your use of the Services. If you wish to access or amend any
        information or PII we hold about you, you may contact us as set forth in the “Contact”
        section. You may further control your PII in the following ways:
      </p>
      <ol className="list-[lower-alpha]">
        <li>
          At your request, we will have any reference to you deleted or blocked from our records.
          We may retain some information you submit for backups, archiving, prevention of fraud and
          abuse, analytics, satisfaction of legal obligations, or where we otherwise reasonably
          believe there is a legitimate reason to do so.
        </li>
        <li>
          You may delete your account information at any time. You may also submit a request to
          delete the PII we maintain about you by contacting us as set forth in the “Contact”
          section below. When you delete your account, your profile is no longer visible to other
          users and dissociated from content you posted under that account. Please note that the
          posts, comments, and messages you submitted prior to deleting your account may still be
          visible to others unless you first delete the specific content.
        </li>
        <li>
          You may control the Services’ use of cookies. Most web browsers are set to accept cookies
          by default. If you prefer, you can usually choose to set your browser to remove or reject
          first- and third-party cookies. Please note that if you choose to remove or reject
          cookies, this could affect the availability and functionality of some aspects of the
          Services.
        </li>
        <li>
          You may, at any time, decline to share certain PII with us, in which case we may not be
          able to provide to you some of the features and functionality of the Services.
        </li>
        <li>
          At any time, you may object to the processing of your PII, on legitimate grounds, except
          if otherwise permitted by applicable law. You always have a right to lodge a complaint
          with data protection authorities.
        </li>
        <li>
          If you receive commercial emails, text messages, or notifications from us, you may
          unsubscribe or opt-out at any time by contacting us as set forth in the “Contact”
          section. Please be aware that it may take up to 10 business days for us to process your
          request. Additionally, even after you opt-out from receiving commercial messages, you may
          continue to receive administrative messages regarding the Services.
        </li>
        <li>
          With your consent, we may send promotional and non-promotional push notifications or
          alerts to your mobile device. You can deactivate these messages at any time by changing
          the notification settings on your mobile device.
        </li>
        <li>
          You can control how we use location information inferred from your IP address and mobile
          device by changing the settings in your mobile device and web browser.
        </li>
        <li>
          We may request access or permission to certain features from your mobile device,
          including your mobile device’s storage. If you wish to change our access or permissions,
          you may do so in your device’s settings.
        </li>
      </ol>
    </section>

    {/* 5. Administrator Access */}
    <section>
      <h2>5. Administrator Access</h2>
      <p>
        You understand and agree that administrators connected with your particular facility, as
        applicable, may have limited access to view your PII, including but not limited to, name,
        email, and such information shared by you in connection with uploading licenses and
        certification for verification. Administrators are bound by additional terms and conditions
        regarding the use and protection of such information.
      </p>
    </section>

    {/* 6. Third Party Websites and Applications */}
    <section>
      <h2>6. Third Party Websites and Applications</h2>
      <p>
        This Privacy Policy applies only to the Services and not to the websites or applications of
        partners, affiliates, or other third-parties. You may be required to accept additional
        policies prior to your use of links accessed through the Services. We are not responsible
        for the content or privacy and security practices and policies of third-party sites or
        services to which links or access are provided through the Services. We encourage you to
        learn about third parties’ privacy and security policies before providing them with
        information.
      </p>
    </section>

    {/* 7. Third Party Servers */}
    <section>
      <h2>7. Third Party Servers</h2>
      <p>
        Certain information stored or transmitted by us hereunder, including PII, may be stored,
        managed, or otherwise handled by third parties. You understand and agree that we are not
        liable, accountable, or otherwise responsible for information communicated to or through
        third party servers and you hereby release us from any associated liability or
        responsibility consistent with the indemnifications, liability limitations, and privacy
        policy included herein. If you are located in the European Union or European Economic Area,
        please note that we will not transfer your information, including PII, to a country outside
        of the European Union or European Economic Area.
      </p>
    </section>

    {/* 8. Data Security */}
    <section>
      <h2>8. Data Security</h2>
      <p>
        We follow generally accepted standards to protect the information submitted to us, both
        during transmission and once we receive it. We maintain appropriate administrative,
        technical, and physical safeguards to protect PII against accidental or unlawful
        destruction, accidental loss, unauthorized alteration, unauthorized disclosure or access,
        misuse, and any other unlawful form of processing of the PII in our possession. This may
        include, for example, firewalls, SSL technology, application-layer security features,
        password protection, and other access and authentication controls. However, no method of
        transmission over the Internet, or method of electronic storage, is 100% secure. We cannot
        ensure or warrant the security of any information you transmit to us and you do so at your
        own risk. We also cannot guarantee that such information may not be accessed, disclosed,
        altered, or destroyed by breach of any of our physical, technical, or managerial
        safeguards. If you believe your PII has been compromised, please contact us at the address
        provided in the “Contact” section. If we learn of a security systems breach, we will inform
        you and the authorities of the occurrence of the breach in accordance with applicable law.
      </p>
    </section>

    {/* 9. Data Retention */}
    <section>
      <h2>9. Data Retention</h2>
      <p>
        We only retain the PII collected from you for a limited period of time as long as we need
        such data to fulfill the purposes for which it was initially collected, unless otherwise
        required by law. If you would like to delete your personal information, you can delete your
        account at any time. It may take additional time for your personal data to be de-indexed
        from search engines, depending on those search engines’ practices, over which we may have
        limited or no control. We will retain and use information as necessary to comply with legal
        obligations, resolution of disputes, and enforcement of our agreements.
      </p>
    </section>

    {/* 10. EU-US Privacy */}
    <section>
      <h2>10. EU-US Privacy</h2>
      <p>
        We make all best reasonable efforts to comply with the EU General Data Protection
        Regulation framework as set forth by the European Union regarding the collection, use, and
        retention of personal data from European Union member countries. We adhere to the
        requirements of notice, choice, onward transfer, security, data integrity, access, and
        enforcement. We are hosted in the United States and by using the Services you authorize us
        to transfer, store, and use your PII in the United States and any other country where we
        operate. Users in the European Economic Area have the right to request access to,
        rectification of, or erasure of their personal data; to data portability in certain
        circumstances; to request restriction of processing; to object to processing; and to
        withdraw consent for processing where they have previously provided consent. These rights
        can be exercised by contacting us as set forth in the “Contact” section.
      </p>
    </section>

    {/* 11. California Residents */}
    <section>
      <h2>11. California Residents</h2>
      <p>
        The California Consumer Privacy Act provides some California residents with the additional
        rights listed below.
      </p>

      <ol className="list-[lower-alpha]">
        {/* a. Right to Know. */}
        <li>
          <b>Right to Know.</b> You have the right to know and see what data we have collected
          about you over the past 12 months, including:
          <ol className="list-[lower-roman]">
            <li>The categories of personal information we have collected about you;</li>
            <li>The categories of sources from which the personal information is collected;</li>
            <li>The business or commercial purpose for collecting your personal information;</li>
            <li>
              The categories of third parties with whom we have shared your personal information;
              and
            </li>
            <li>The specific pieces of personal information we have collected about you.</li>
          </ol>
        </li>

        {/* b. Right to Delete. */}
        <li>
          <b>Right to Delete.</b> You have the right to request that we delete the personal
          information we have collected from you (and direct our service providers to do the same).
          There are a number of exceptions, however, that include, but are not limited to, when the
          information is necessary for us or a third party to do any of the following:
          <ol className="list-[lower-roman]">
            <li>Complete a transaction;</li>
            <li>Provide you a good or service:</li>
            <li>Perform a contract between us and you;</li>
            <li>Protect your security and prosecute those responsible for breaching it;</li>
            <li>Fix our system in the event of a bug, virus, or malware;</li>
            <li>Protect the free speech rights of you or other users;</li>
            <li>
              Comply with the California Electronic Communications Privacy Act (Cal. Penal Code
              §1546 et seq.);
            </li>
            <li>
              Engage in public or peer-reviewed scientific, historical, or statistical research in
              the public interests that adheres to all other applicable ethics and privacy laws;
            </li>
            <li>Comply with a legal obligation; or</li>
            <li>
              Make other internal and lawful uses of the information that are compatible with the
              context in which you provided it.
            </li>
          </ol>
        </li>

        {/* c. Other Rights. */}
        <li>
          <b>Other Rights.</b> You can request certain information about any disclosure of personal
          information to third parties for their own direct marketing purposes during the preceding
          calendar year. This request is free and may be made once a year. You also have the right
          not to be discriminated against for exercising any of the rights listed above.
        </li>

        {/* d. Exercising your California Privacy Rights. */}
        <li>
          <b>Exercising your California Privacy Rights.</b> To request access to or deletion of
          your PII, or to exercise any other data rights under California law, please contact us as
          set forth in the “Contact” section.
        </li>
      </ol>
    </section>

    {/* 12. Changes and Updates to This Privacy */}
    <section>
      <h2>12. Changes and Updates to This Privacy</h2>
      <p>
        Please revisit this page periodically to stay aware of any changes to this Privacy Policy,
        which we may update from time to time. If we modify the Privacy Policy, we will provide
        notice through the Services, indicate the date of the latest revision, and will comply with
        applicable law. Your continued use of the Services after the revised Privacy Policy has
        become effective indicates that you have read, understood, and agreed to the current
        version of the Privacy Policy.
      </p>
    </section>

    {/* 13. Contact Us */}
    <section>
      <h2>13. Contact Us</h2>
      <address>
        <b>Rescue X Training and Consulting, LLC</b>
        <br />
        1312 17th Street #863
        <br />
        Denver, CO 80202
        <br />
        📧 <a href="mailto:info@rescuextraining.com">info@rescuextraining.com</a>
      </address>
    </section>
  </SharedLegalLayout>
)

export default PrivacyPolicy
